import { z } from 'zod';
// B3: the shadow column's vocabulary (PURE — no three). Three answers cover this table: a PROP
// casts, receives and is static; an editor-only GHOST does nothing with shadows; the pot is the one
// thing here that moves (its shards fly). The companion's row is in the exploration sub-table.
import { ENTITY_SHADOW } from '../render/lights/lightModel';
import { EXPLORATION_ENTITIES } from './exploration/table';
import { MENU_ENTITIES } from './menu/table';
// F1: the ONE placement-rotation fragment every placeable class spreads. It lives in
// `world/schema.ts` (pure, the zod precedent) rather than here because the per-vertical sub-tables
// need it too, and importing a VALUE out of this module would close a cycle — this file imports
// their tables at module top, so they would read `undefined` at evaluation time.
import { PLACEMENT_ROTATION } from '../world/schema';
import type { EntityMeta, EntityModule } from './types';

/**
 * The entity registry TABLE (ADR-0001: deploy whole, download partial). This is the ONLY eagerly
 * imported runtime file in src/entities/: names + zod schemas + import THUNKS. Each entity class
 * stays behind `() => import(...)` so Vite splits it into its own content-hashed chunk — one
 * careless static import drags the class into the main bundle for every player
 * (tests/entityBoundary.test.ts + scripts/checkBundle.mjs keep that mistake loud).
 *
 * Worlds reference these classes by name in data ({"type": "destructible_pot", ...}) and never
 * contain code. Adding a class = schema + one lazy line here + the module file (see CLAUDE.md
 * recipe). NEVER a static import of the module.
 */

export const DestructiblePotSchema = z.object({
  type: z.literal('destructible_pot'),
  /** Base of the pot, world meters ([x, y, z] — y sits on the ground it stands on). */
  pos: z.tuple([z.number(), z.number(), z.number()]),
  /** Seconds until the pot pops back after a break. */
  respawnS: z.number().positive().default(8),
  /** Trigger radius, meters (sensor only — pots never get a physics collider). */
  radius: z.number().positive().default(0.35),
});

export type DestructiblePotDef = z.infer<typeof DestructiblePotSchema>;

export const PlayerStartSchema = z.object({
  type: z.literal('player_start'),
  /** Feet position, world meters. */
  pos: z.tuple([z.number(), z.number(), z.number()]),
  /** Facing + tilt (F1 — the shared `PLACEMENT_ROTATION`); `yawDeg` 0 = game forward (−Z). */
  ...PLACEMENT_ROTATION,
  /** Spawn-point name — boot prefers `'default'` (src/world/spawnPoint.ts resolution order). */
  id: z.string().default('default'),
});

export type PlayerStartDef = z.infer<typeof PlayerStartSchema>;

export const StaticMeshSchema = z.object({
  type: z.literal('static_mesh'),
  /** Placement origin, world meters (the GLB's own origin lands here). */
  pos: z.tuple([z.number(), z.number(), z.number()]),
  /**
   * ⭐ ROTATION — yaw, pitch AND roll (F1, the shared `PLACEMENT_ROTATION`).
   *
   * Yaw-only was not a simplification, it was a limit: the Mars census found 720 of 5,564
   * placements (12.9 %) carrying pitch or roll, and a yaw-only schema lays every one of them flat
   * without erroring. Additive and `worldHash`-safe for the same reason `scale` and `collision`
   * are — placements are stored RAW (`z.array(z.unknown())`) so these defaults materialise at
   * validation and are never written back into a baked world's file.
   */
  ...PLACEMENT_ROTATION,
  /**
   * ⭐ SCALE — ONE NUMBER (uniform) **or** `[x, y, z]` (non-uniform). ADDITIVE by construction: a
   * number still means what it always meant, so `sponza-debug-01`'s `"scale": 2` parses to the
   * same value and hashes to the same `worldHash` (pinned in `tests/renderLightRig.test.ts`). The
   * `.default(1)` is hash-safe for the same reason `collision`'s is — `WorldSchema.entities` is
   * `z.array(z.unknown())`, so placements are stored RAW and this default is materialised at
   * validation, never written back into `world.json`.
   *
   * ⚠️ Downstream there is exactly ONE representation: `world/collision.scaleVec3` folds either
   * spelling to a `[x, y, z]`, and `placementPose` is the only door the runtime uses. Do not read
   * `def.scale` directly and branch on its type — that is two answers to "how big is this".
   *
   * ⚠️ AND IT IS NOT FREE IN PHYSICS. A Rapier collider carries no scale, so the scale is baked
   * into the collider's points (`transformShapes`): exact for a hull and a trimesh, exact for a
   * box whose scaled basis is still orthogonal, and IMPOSSIBLE for a `USP_` sphere or a `UCP_`
   * capsule (they become ellipsoids). Those are REFUSED per shape with a sentence rather than
   * approximated — see `transformShapes`'s refusal channel.
   */
  /*
   * ⭐ F2 — NON-ZERO, NOT POSITIVE. A negative component is how every level editor spells a
   * mirrored placement, and 633 of Mars's 5,564 carry one (627 of those have a negative
   * DETERMINANT and are truly mirrored — see `scaleVec3` for why both numbers exist). This was
   * `.positive()`, so those placements did not merely lose their mirror — they FAILED VALIDATION.
   * Zero is still refused: a zero-scaled mesh is invisible and its collider degenerate, and no
   * authoring tool means it. See `scaleVec3` for the rendering-rule-3 cost and why it does not
   * veto this.
   */
  scale: z
    .union([
      z.number().refine((n) => n !== 0, 'scale may not be 0'),
      z.tuple([
        z.number().refine((n) => n !== 0, 'scale may not be 0'),
        z.number().refine((n) => n !== 0, 'scale may not be 0'),
        z.number().refine((n) => n !== 0, 'scale may not be 0'),
      ]),
    ])
    .default(1),
  /** WORLD MESH ASSET id (the world's `assets` manifest, kind 'mesh') — resolved via EntityCtx. */
  asset: z.string(),
  description: z.string().optional(),
  /**
   * ⭐ COLLISION MODE (EDITOR v2 · S2 — DATA ONLY UNTIL S5). Which collider this PLACEMENT gets:
   * `asset` defers to the asset's own declaration (`WorldFileAssetSchema.collision`, absent ⇒
   * trimesh for a mesh), the other three override it per placement. CONSUMED SINCE S5: the entity
   * runs the ONE resolver (`world/collision.resolveCollisionMode`) at load and publishes the
   * shapes; the host binds them to Rapier. The `.default('asset')` is HASH-SAFE only
   * because `WorldSchema.entities` is `z.array(z.unknown())`: placements are stored raw and this
   * default is materialised at validation time, never into `world.json` (`tests/schema.test.ts`
   * says it out loud). A `.default` on a hashed block would orphan every bake on disk.
   */
  collision: z.enum(['asset', 'trimesh', 'box', 'none']).default('asset'),
});

export type StaticMeshDef = z.infer<typeof StaticMeshSchema>;

/**
 * ⭐ P1.5·C — THE REFERENCE CODE-BEARING CLASS (plan §13.6). A library `entity` asset names it and
 * supplies these params; a world places it either directly (`"type": "solar_system_sim"`) or
 * through a library prefab (`"type": "lib:<id>"`), and the merge is ADR-0002's one precedence rule
 * in `world/prefabs.resolveEntityDef` either way. The class itself knows NOTHING about the library
 * beyond `params.material` being an id it hands to `EntityCtx.assets` — a registry class is a
 * capability the runtime shipped, and the asset is data that names it.
 */
export const SolarSystemSimSchema = z.object({
  type: z.literal('solar_system_sim'),
  /** The sun's centre, world meters. */
  pos: z.tuple([z.number(), z.number(), z.number()]),
  /** Orbital-plane tilt about +Z, degrees — a system that is not flat-on reads as a system. */
  tiltDeg: z.number().default(15),
  /** Radius of the OUTERMOST orbit, meters. Inner orbits are a fixed fraction of it. */
  radiusM: z.number().positive().default(3),
  /** How many planets. One `InstancedMesh`, so this is a buffer size and not a draw count. */
  planets: z.number().int().min(1).max(64).default(6),
  /** The sun's radius, meters. */
  sunRadiusM: z.number().positive().default(0.6),
  /** The outermost planet's radius, meters; the rest are a deterministic fraction of it. */
  planetRadiusM: z.number().positive().default(0.22),
  /** Orbits per second for the innermost body, before the per-orbit falloff. */
  speed: z.number().default(0.06),
  /**
   * A library `kind: material` asset id whose SHARED `SurfaceMaterial` skins the planets, or null
   * for the stock skin. It is a plain string in world data — the class hands it to
   * `EntityCtx.assets.acquire`, and an id this catalog does not carry degrades with one warning
   * (rule 6), never a throw.
   */
  material: z.string().nullable().default(null),
});

export type SolarSystemSimDef = z.infer<typeof SolarSystemSimSchema>;

export interface RegistryEntry {
  /** Validates one world-data placement (the whole def object, `type` included). */
  schema: z.ZodType;
  /** Display metadata (ADR-0002) — REQUIRED; the editor derives all grouping/browsing from it. */
  meta: EntityMeta;
  /** Lazy chunk thunk — the only road to the class. */
  load: () => Promise<EntityModule>;
}

export const ENTITY_REGISTRY: Record<string, RegistryEntry> = {
  destructible_pot: {
    schema: DestructiblePotSchema,
    meta: {
      category: 'interactable',
      label: 'Destructible pot',
      description: 'Breaks on fast or rolling contact; respawns.',
      shadow: ENTITY_SHADOW.movingProp,
      mobility: 'dynamic',
    },
    load: () => import('./destructiblePot'),
  },
  player_start: {
    schema: PlayerStartSchema,
    meta: {
      category: 'logic',
      label: 'Player start',
      description: 'Where the player spawns (and respawns). Editor-only ghost marker; no gameplay.',
      shadow: ENTITY_SHADOW.ghost,
      mobility: 'static',
    },
    load: () => import('./playerStart'),
  },
  static_mesh: {
    schema: StaticMeshSchema,
    meta: {
      category: 'decor',
      label: 'Static mesh',
      description: 'A behavior-free prop from a world mesh asset (GLB). Collides per its collision mode (S5).',
      // The GLB lands under the group seconds after `create()`; the applier's `childadded` hook is
      // what reaches it — `adaptGltfScene` no longer writes the flags itself.
      shadow: ENTITY_SHADOW.prop,
      mobility: 'static',
    },
    load: () => import('./staticMesh'),
  },
  solar_system_sim: {
    schema: SolarSystemSimSchema,
    meta: {
      /*
       * ⭐ `decor` AND THE CHOICE IS ARGUED, NOT DEFAULTED. The five categories (ADR-0002) group
       * the editor's palette by what an author is LOOKING FOR. A solar system sim is a visible,
       * self-driving prop: nothing the player does changes it, so it is not `interactable`, and
       * `logic` is the invisible machinery tier (`player_start`, triggers, directors). It is
       * `decor` with behaviour — which is precisely what "a code-bearing asset" means and exactly
       * why the taxonomy did not have to grow to hold one.
       */
      category: 'decor',
      label: 'Solar system sim',
      description:
        'A sun and N planets on circular orbits — the reference code-bearing library asset. ' +
        'Optionally skinned by a library material.',
      // It casts and receives like any prop; nothing in it moves the camera or the player, and the
      // planets are an InstancedMesh, which the shadow pass handles as one object.
      shadow: ENTITY_SHADOW.prop,
      // ⚠️ DYNAMIC despite the `prop` shadow column — the two are DIFFERENT QUESTIONS and this
      // class is the clearest proof. It casts and receives like any prop AND its planets orbit
      // every frame. A first pass at this table derived mobility from the shadow flag and got this
      // row, the bobbing anchor marker and the station's hinged lid all wrong.
      mobility: 'dynamic',
    },
    load: () => import('./solarSystemSim'),
  },
  // Sub-table composition (ADR-0001 amendment, X0): each vertical contributes an eager-safe
  // sub-table (schemas + meta + lazy thunks — same rules as this file) spread in with ONE line,
  // so parallel tracks never conflict on this shared file again.
  ...EXPLORATION_ENTITIES,
  // …and the MAIN-MENU vertical's (main-menu plan §4). One line, same contract: the menu's load
  // trigger (`src/ui/menu/gate.ts`) is a predicate over THIS table's keys, never a string literal.
  ...MENU_ENTITIES,
};

/** Registered class names — drives the editor palette and world `requires` checks. */
export const entityTypeNames = (): string[] => Object.keys(ENTITY_REGISTRY);
